/**
 * Pipedream Connect helpers for Snagcart /connections.
 *
 * Managed OAuth for any catalog provider whose Pipedream name_slug is available
 * (from GET /api/connections/pipedream/status → app_by_provider).
 * API-key / BYOK / Evolution WhatsApp stay on the vault path.
 *
 * Use Connect Link as a true top-level tab navigation (never SDK iframe /
 * connectAccount). Framing pipedream.com/_static/connect.html under COEP
 * surfaces as "pipedream.com refused to connect."
 *
 * Desktop Connect Link defaults to a nested OAuth popup; we force popups=false
 * so OAuth runs as same-tab navigation (return_mode=connect_redirect), then
 * success_redirect_uri returns to /connections for polling/complete.
 */

/**
 * Synthetic provider-id prefix the backend assigns to Pipedream catalog apps that
 * have no curated Snagcart provider (see PIPEDREAM_PROVIDER_PREFIX in
 * backend/pipedream_connect.py). The suffix is the Pipedream name_slug.
 */
export const PIPEDREAM_PROVIDER_PREFIX = 'pipedream--';

/** Canonical Pipedream name_slug form: lowercase, underscores. */
export function normalizePipedreamSlug(raw) {
  return String(raw || '').trim().toLowerCase().replace(/-/g, '_');
}

/** Pipedream name_slug encoded in a `pipedream--<slug>` provider id, else null. */
export function pipedreamSlugFromProviderId(providerId) {
  const id = String(providerId || '').trim().toLowerCase();
  if (!id.startsWith(PIPEDREAM_PROVIDER_PREFIX)) return null;
  return normalizePipedreamSlug(id.slice(PIPEDREAM_PROVIDER_PREFIX.length)) || null;
}

/** @deprecated Prefer status.app_by_provider from the backend. */
export const PIPEDREAM_APP_BY_PROVIDER = {
  'slack-oauth': 'slack',
  hubspot: 'hubspot',
  gmail: 'gmail',
  notion: 'notion',
  'google-sheets': 'google_sheets',
};

const LOCAL_DEV_ORIGINS = [
  'http://localhost:3000',
  'http://127.0.0.1:3000',
  'http://localhost:3001',
  'http://127.0.0.1:3001',
];

export const PIPEDREAM_SIGN_IN_URL = 'https://pipedream.com/auth/login';

const DEV_CONNECT_HINT =
  'Development mode requires you to be signed in on the OAuth provider portal in this browser, stay under the 10 external-user limit, and use one origin consistently (localhost or 127.0.0.1 — not both).';

const DEV_CONNECT_CLOSED_HINT =
  'Most common cause: you are not signed in on the OAuth provider portal in this browser. Open the sign-in page, sign in, leave that tab open, then click Connect again. Also stay under the 10 external-user limit and use one origin (localhost or 127.0.0.1 — not both).';

/**
 * Resolve Pipedream app slug from status payload (preferred) or static fallback.
 */
export function pipedreamAppForProvider(providerId, pipedreamStatus = null) {
  if (!providerId) return null;
  const id = String(providerId).toLowerCase();
  const fromStatus = pipedreamStatus?.app_by_provider?.[id]
    || pipedreamStatus?.app_by_provider?.[providerId];
  if (fromStatus) return fromStatus;
  if (Array.isArray(pipedreamStatus?.oauth_provider_ids)
    && pipedreamStatus.oauth_provider_ids.map((x) => String(x).toLowerCase()).includes(id)) {
    // Status lists the provider but map missing — use static fallback / id heuristic
    return PIPEDREAM_APP_BY_PROVIDER[id] || id.replace(/-/g, '_');
  }
  if (pipedreamStatus?.enabled === false) return null;
  // Catalog-only Pipedream app: the slug is encoded in the id, so it stays
  // connectable even when the status map is cold or was built before the
  // backend's apps cache warmed up.
  const prefixed = pipedreamSlugFromProviderId(id);
  if (prefixed) return prefixed;
  if (pipedreamStatus && pipedreamStatus.app_by_provider) {
    // Explicit empty map entry means not connectable
    return null;
  }
  return PIPEDREAM_APP_BY_PROVIDER[id] || null;
}

/** True when status says this provider can use Pipedream Connect. */
export function canConnectViaPipedream(providerId, pipedreamStatus) {
  if (!pipedreamStatus?.enabled || !providerId) return false;
  const id = String(providerId).toLowerCase();
  if (pipedreamSlugFromProviderId(id)) return true;
  if (Array.isArray(pipedreamStatus.oauth_provider_ids)) {
    return pipedreamStatus.oauth_provider_ids
      .map((x) => String(x).toLowerCase())
      .includes(id);
  }
  if (pipedreamStatus.app_by_provider
    && Object.prototype.hasOwnProperty.call(pipedreamStatus.app_by_provider, id)) {
    return Boolean(pipedreamStatus.app_by_provider[id]);
  }
  return Boolean(pipedreamAppForProvider(providerId, pipedreamStatus));
}

/** @deprecated Use canConnectViaPipedream(providerId, status). */
export function isPipedreamPilotProvider(providerId, pipedreamStatus = null) {
  if (pipedreamStatus) return canConnectViaPipedream(providerId, pipedreamStatus);
  return Boolean(pipedreamAppForProvider(providerId));
}

function normalizeConnectError(err) {
  if (err instanceof Error) return err;
  if (typeof err === 'string' && err.trim()) return new Error(err);
  const msg =
    err?.message
    || err?.error
    || err?.detail
    || (typeof err?.toString === 'function' ? err.toString() : '');
  return new Error(msg && msg !== '[object Object]' ? msg : 'Connect failed');
}

/**
 * Read Connect Link / OAuth error detail from redirect query or hash.
 * Pipedream Connect Link often redirects without params; hash/query may still
 * carry error / error_description / message when present.
 */
export function extractPipedreamRedirectError(searchParams, hash = '') {
  const fromParams = (sp) => {
    if (!sp) return '';
    const get = (k) => {
      try {
        return (sp.get?.(k) || '').trim();
      } catch {
        return '';
      }
    };
    return (
      get('error_description')
      || get('errorDescription')
      || get('message')
      || get('error_message')
      || get('errorMessage')
      || get('err')
      || get('error')
      || ''
    );
  };

  let msg = '';
  if (searchParams) {
    msg = fromParams(searchParams);
  }
  if (!msg && hash) {
    const raw = String(hash).replace(/^#/, '');
    try {
      msg = fromParams(new URLSearchParams(raw));
    } catch {
      /* ignore */
    }
  }
  if (!msg) return '';
  // Ignore our own flag values mistaken as errors
  if (['0', '1', 'error', 'success', 'true', 'false'].includes(msg.toLowerCase())) {
    return '';
  }
  return msg;
}

export function formatPipedreamConnectError(detail, { development = true } = {}) {
  let base = (detail || '').trim() || 'Connect reported an error';
  // Never surface the underlying provider brand name in user-facing copy.
  base = base
    .replace(/Pipedream Connect/gi, 'Connect')
    .replace(/Pipedream/gi, 'Connect')
    .replace(/pipedream\.com/gi, 'the OAuth provider portal');
  if (!development) return base;
  if (/Must be signed in/i.test(base)) {
    return `${base} Open the sign-in page, sign in, then click Connect again.`;
  }
  if (/closed without a new account/i.test(base)) {
    return `${base}. ${DEV_CONNECT_CLOSED_HINT}`;
  }
  if (/external.user|LIMIT_MAX|signed in|OAuth provider portal/i.test(base)) return base;
  return `${base}. ${DEV_CONNECT_HINT}`;
}

export function collectAllowedOrigins(extra = []) {
  const origins = new Set();
  if (typeof window !== 'undefined' && window.location?.origin) {
    origins.add(window.location.origin);
  }
  for (const o of LOCAL_DEV_ORIGINS) origins.add(o);
  for (const o of extra || []) {
    if (o) origins.add(String(o).trim());
  }
  return [...origins].filter(Boolean);
}

/** Hostname only — safe for UI debug toasts (never the raw token). */
export function connectLinkHostname(url) {
  try {
    return new URL(url).hostname || 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Build the hosted Connect Link URL from a create-token response.
 * Prefers connect_link_url (snake) / connectLinkUrl (camel); always ensures
 * connectLink=true + app= + popups=false for same-tab OAuth.
 */
export function buildConnectLinkUrl(tokenRes, app) {
  const applyParams = (url) => {
    url.searchParams.set('app', app);
    url.searchParams.set('connectLink', 'true');
    // Desktop Connect Link otherwise opens a nested OAuth popup; same-tab
    // redirect is more reliable when Connect already runs in our popup.
    url.searchParams.set('popups', 'false');
    return url.toString();
  };

  const base = (
    tokenRes?.connect_link_url
    || tokenRes?.connectLinkUrl
    || ''
  ).trim();

  if (base) {
    try {
      return applyParams(new URL(base));
    } catch {
      const join = base.includes('?') ? '&' : '?';
      return `${base}${join}app=${encodeURIComponent(app)}&connectLink=true&popups=false`;
    }
  }

  const token = tokenRes?.token;
  if (!token) throw new Error('Connect token missing from server response');
  return applyParams(
    new URL(
      `https://pipedream.com/_static/connect.html?token=${encodeURIComponent(token)}`,
    ),
  );
}

function accountIdFromMessage(data) {
  return data?.authProvisionId || data?.id || data?.account?.id || null;
}

async function pollForNewAccount(listAccounts, priorAccountIds, {
  attempts = 8,
  delayMs = 700,
} = {}) {
  const prior = priorAccountIds || new Set();
  let lastAccounts = [];
  for (let i = 0; i < attempts; i += 1) {
    if (i > 0) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
    lastAccounts = (await listAccounts()) || [];
    const created = lastAccounts.find((a) => a?.id && !prior.has(a.id));
    if (created?.id) return created;
  }
  // Prefer a newly created account; otherwise use the newest account for this
  // app. Covers reconnect / missed /complete when the Slack account already
  // existed on Pipedream before this Connect attempt.
  if (lastAccounts[0]?.id) return lastAccounts[0];
  return null;
}

/**
 * Navigate an already-opened tab to Connect Link and resolve with { id }.
 * Listens for postMessage from pipedream.com / our redirect; on close, polls listAccounts.
 *
 * IMPORTANT: do not pass noopener — we need the Window ref for closed polling
 * and success_redirect_uri → window.opener.postMessage.
 */
async function connectViaOpenedWindow({
  popup,
  connectUrl,
  listAccounts,
  priorAccountIds,
  onError,
  environment,
}) {
  return new Promise((resolve, reject) => {
    try {
      // Top-level navigation in the child tab (not an iframe embed).
      popup.location.replace(connectUrl);
    } catch (err) {
      const error = normalizeConnectError(err);
      if (onError) onError(error);
      reject(error);
      return;
    }

    let settled = false;
    const cleanupFns = [];

    const settle = (fn, value) => {
      if (settled) return;
      settled = true;
      cleanupFns.forEach((c) => {
        try { c(); } catch { /* ignore */ }
      });
      try { if (!popup.closed) popup.close(); } catch { /* ignore */ }
      fn(value);
    };

    const finishAfterClose = async ({ preferError } = {}) => {
      if (settled) return;
      try {
        const newest = await pollForNewAccount(listAccounts, priorAccountIds);
        if (newest?.id) {
          settle(resolve, { id: newest.id, ...newest });
          return;
        }
        const detail = preferError
          || 'Connect closed without a new account';
        const error = new Error(formatPipedreamConnectError(detail, {
          development: environment === 'development',
        }));
        if (onError) onError(error);
        settle(reject, error);
      } catch (err) {
        const error = normalizeConnectError(err);
        if (onError) onError(error);
        settle(reject, error);
      }
    };

    const onMessage = (e) => {
      const origin = String(e.origin || '');
      if (!origin.includes('pipedream.com') && origin !== window.location.origin) {
        return;
      }
      const data = e.data || {};
      const type = data.type || data.event;
      if (type === 'success' || type === 'pipedream-connect-success') {
        const id = accountIdFromMessage(data);
        if (id) {
          settle(resolve, { id });
          return;
        }
        finishAfterClose();
        return;
      }
      if (type === 'error' || type === 'pipedream-connect-error') {
        const detail = data.error || data.message || data.error_description || '';
        // Still poll briefly — Connect may have saved the account despite redirect noise
        finishAfterClose({
          preferError: formatPipedreamConnectError(detail, {
            development: environment === 'development',
          }),
        });
      }
    };

    window.addEventListener('message', onMessage);
    cleanupFns.push(() => window.removeEventListener('message', onMessage));

    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer);
        finishAfterClose();
      }
    }, 400);
    cleanupFns.push(() => clearInterval(timer));
  });
}

/**
 * Open Pipedream managed OAuth for a provider.
 * Mints a Connect token, then opens Connect Link as top-level navigation in a new tab.
 */
export async function connectViaPipedream({
  apiFetch,
  providerId,
  environment = 'development',
  pipedreamStatus = null,
  app: appOverride = null,
  onSuccess,
  onError,
  onConnectUrl,
}) {
  const app = appOverride
    || pipedreamAppForProvider(providerId, pipedreamStatus);
  if (!app) {
    throw new Error(`Provider '${providerId}' is not enabled for managed OAuth`);
  }

  // Must open during the user-gesture turn — async mint would be popup-blocked.
  const popup = window.open('about:blank', 'pipedream-connect');
  if (!popup) {
    const error = new Error(
      'Popup blocked — allow popups for this site, then try Connect again',
    );
    if (onError) onError(error);
    throw error;
  }

  try {
    popup.document.write(
      '<!doctype html><title>Connect</title><p style="font:14px system-ui;padding:24px">Opening Connect…</p>',
    );
    popup.document.close();
  } catch {
    /* cross-origin or closed — ignore */
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const allowedOrigins = collectAllowedOrigins();

  const mintToken = async () => {
    // success_redirect lands back in the popup so opener can poll/complete.
    // Intentionally omit error_redirect_uri: Connect Link redirects to it
    // without appending the real error message, which hid Pipedream's
    // development-mode failures (signed-in requirement, 10-user limit, etc.).
    const successRedirect = origin
      ? `${origin}/connections?pipedream_connected=1&app=${encodeURIComponent(app)}`
      : undefined;
    const tokenRes = await apiFetch('/api/connections/pipedream/token', {
      method: 'POST',
      body: JSON.stringify({
        provider: providerId,
        allowed_origins: allowedOrigins,
        success_redirect_uri: successRedirect,
      }),
    });
    if (!tokenRes?.token) {
      throw new Error('Connect token missing from server response');
    }
    return tokenRes;
  };

  const listAccounts = async () => {
    const res = await apiFetch(`/api/connections/pipedream/accounts?app=${encodeURIComponent(app)}`);
    return res?.accounts || [];
  };

  let priorIds = new Set();
  try {
    const existing = await listAccounts();
    priorIds = new Set(existing.map((a) => a?.id).filter(Boolean));
  } catch {
    priorIds = new Set();
  }

  let tokenRes;
  try {
    tokenRes = await mintToken();
  } catch (err) {
    try { popup.close(); } catch { /* ignore */ }
    throw err;
  }

  const envName = tokenRes.environment || environment;
  if (!tokenRes.external_user_id) {
    try { popup.close(); } catch { /* ignore */ }
    throw new Error('Connect token response missing external_user_id');
  }

  const connectUrl = buildConnectLinkUrl(tokenRes, app);
  if (typeof onConnectUrl === 'function') {
    try { onConnectUrl(connectUrl, connectLinkHostname(connectUrl)); } catch { /* ignore */ }
  }

  const account = await connectViaOpenedWindow({
    popup,
    connectUrl,
    listAccounts,
    priorAccountIds: priorIds,
    onError,
    environment: envName,
  });

  if (onSuccess) await onSuccess(account, tokenRes);
  return account;
}
